import { components, ControlProps } from 'react-select';
import styled from 'styled-components';

import { InfoPopover } from '../../../common/InfoPopover/InfoPopover';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { Optional } from '../../Input/components/Optional';
import { Required } from '../../Input/components/Required';
import { Text } from '../../Text/Text';
import { SelectProps } from '../Select';

const LabelText = styled.div<Pick<ControlProps, 'menuIsOpen' | 'isDisabled'>>`
    color: ${(props) => {
        if (props.isDisabled) {
            return theme.colors.foreground.disabled;
        }

        return theme.colors.foreground.default;
    }};
    font-size: ${theme.fontSizes[14]};
    font-weight: ${theme.fontWeights[500]};
    line-height: 24px;
    transition: color 75ms;
`;

interface CustomControlProps<Option, IsMulti extends boolean>
    extends ControlProps<Option, IsMulti>,
        Pick<SelectProps<Option>, 'label'> {
    markAsRequired: boolean;
    markAsOptional: boolean;
    description?: string;
    labelInfo?: string;
}

export function CustomControl<Option, IsMulti extends boolean>({
    label,
    menuIsOpen,
    isDisabled,
    markAsRequired,
    markAsOptional,
    description,
    labelInfo,
    children,
    ...rest
}: CustomControlProps<Option, IsMulti>) {
    return (
        <Box display="flex" flexDirection="column" gap={4}>
            {label ? (
                <Box display="flex" alignItems="center" gap={4}>
                    <LabelText menuIsOpen={menuIsOpen} isDisabled={isDisabled}>
                        {label}
                        {markAsRequired ? <Required disabled={isDisabled} /> : null}
                        {markAsOptional ? <Optional disabled={isDisabled} /> : null}
                    </LabelText>
                    {labelInfo ? (
                        <InfoPopover>
                            <Text variant="extraSmall">{labelInfo}</Text>
                        </InfoPopover>
                    ) : null}
                </Box>
            ) : null}

            {description ? (
                <Text variant="extraSmall" color="foreground.muted">
                    {description}
                </Text>
            ) : null}

            <components.Control {...rest} menuIsOpen={menuIsOpen} isDisabled={isDisabled}>
                {children}
            </components.Control>
        </Box>
    );
}
